import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  Report,
  ReportSection,
  ReportItem,
  ReportMode,
  Severity,
  Photo,
} from '@/types';
import { reportsApi, agreementsApi } from '@/api';
import { useInspectorProfile } from '@/contexts/InspectorProfileContext';
import type { Agreement } from '@/types';
import { sortByOrder, copyToClipboard, formatCostRange, formatDate } from '@/utils/helpers';
import { getPublicOrigin } from '@/utils/booking';
import { LANGUAGE_CONFIGS } from '@/utils/constants';
import { severityKey } from '@/ui/severity';
import CostSummaryPanel from './CostSummaryPanel';
import ConfirmDialog from './ConfirmDialog';
import { Modal, Field, Input, Button } from '@/ui';
import { SectionCard } from './ReportBuilder/SectionCard';
import { AutoSaveProvider, useReportAutoSave } from '@/hooks/useReportAutoSave';
import {
  IconChevRight,
  IconPlus,
  IconFileDoc,
  IconSparkle,
} from './ReportBuilder/_icons';

interface ReportBuilderProps {
  reportId: string;
  onSave?: () => void;
}

const ReportBuilder: React.FC<ReportBuilderProps> = ({ reportId }) => {
  const navigate = useNavigate();
  const { profile } = useInspectorProfile();
  const autoSave = useReportAutoSave();
  const { saveState, lastSavedAt, scheduleSave, runSave, retry } = autoSave;
  const [report, setReport] = useState<Report | null>(null);
  const [mode, setMode] = useState<ReportMode>('buyer');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [costRefreshKey, setCostRefreshKey] = useState(0);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [sendAgentName, setSendAgentName] = useState('');
  const [sendAgentEmail, setSendAgentEmail] = useState('');
  // Free-form input — empty string means "no fee for this delivery". Parsed
  // to number at submit time. Pre-populated from the report's stored value
  // so re-sends remember what was charged last time.
  const [sendFeeAmount, setSendFeeAmount] = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendEmailSkipped, setSendEmailSkipped] = useState(false);
  const [sendAgentSkipped, setSendAgentSkipped] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [agreements, setAgreements] = useState<Agreement[] | null>(null);
  const [sectionPendingDelete, setSectionPendingDelete] = useState<string | null>(null);
  const [itemPendingDelete, setItemPendingDelete] = useState<{ sectionId: string; itemId: string } | null>(null);
  const [aiSummaryText, setAiSummaryText] = useState('');
  const [generatingAiSummary, setGeneratingAiSummary] = useState(false);
  const [aiSummaryError, setAiSummaryError] = useState<string | null>(null);

  useEffect(() => {
    loadReport();
    loadAgreements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const response = await reportsApi.getReport(reportId);
      if (response.success && response.data) {
        setReport(response.data);
        setMode(response.data.mode);
        setAiSummaryText(response.data.aiSummary ?? '');
      }
    } catch (err) {
      setError('Failed to load report');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAgreements = async () => {
    try {
      const resp = await agreementsApi.listForReport(reportId);
      if (resp.success && resp.data) setAgreements(resp.data);
    } catch (err) {
      // Non-fatal — the rest of the editor should still load if this 5xx's.
      console.error('Failed to load agreements', err);
    }
  };

  // Most-recent agreement for this report. Drives the header status pill +
  // the button state (don't double-send if one is pending; show signed PDF
  // link if signed).
  const latestAgreement = useMemo(() => {
    if (!agreements || agreements.length === 0) return null;
    return [...agreements].sort((a, b) => {
      const at = a.sentAt ? Date.parse(a.sentAt) : 0;
      const bt = b.sentAt ? Date.parse(b.sentAt) : 0;
      return bt - at;
    })[0];
  }, [agreements]);

  // handleModeChange removed alongside the hidden Buyer/Seller toggle —
  // `mode` is still loaded from the report and drives LANGUAGE_CONFIGS, but
  // there's no UI affordance to flip it until the toggle's semantics are
  // resolved. Restore here when the segmented control comes back.

  const handleGeneratePdf = async () => {
    // Open tab synchronously (within click handler) to avoid Safari popup blocker
    const pdfTab = window.open('', '_blank');
    if (pdfTab) {
      pdfTab.document.write(`
        <html><head><title>Generating PDF...</title></head>
        <body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:system-ui,sans-serif;color:#44444a;background:#fafaf9;">
          <div style="text-align:center;">
            <div style="border:3px solid #ebebe9;border-top-color:#2F6A44;border-radius:50%;width:36px;height:36px;animation:spin 0.8s linear infinite;margin:0 auto 18px;"></div>
            <p style="font-size:14px;">Generating your inspection report…</p>
            <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
          </div>
        </body></html>
      `);
    }

    try {
      setGeneratingPdf(true);
      const response = await reportsApi.generatePDF(reportId);
      if (response.success && response.data) {
        const { pdfBase64 } = response.data as { pdfBase64?: string; pdfUrl: string };
        if (pdfBase64) {
          const byteChars = atob(pdfBase64);
          const byteArray = new Uint8Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) {
            byteArray[i] = byteChars.charCodeAt(i);
          }
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);
          if (pdfTab) {
            pdfTab.location.href = blobUrl;
          }
        } else if (pdfTab) {
          pdfTab.document.body.innerHTML = '<p style="text-align:center;margin-top:40vh;font-family:system-ui;color:#ef4444;">Failed to generate PDF. Please close this tab and try again.</p>';
        }
      } else if (pdfTab) {
        pdfTab.document.body.innerHTML = '<p style="text-align:center;margin-top:40vh;font-family:system-ui;color:#ef4444;">Failed to generate PDF. Please close this tab and try again.</p>';
      }
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      if (pdfTab) {
        pdfTab.document.body.innerHTML = '<p style="text-align:center;margin-top:40vh;font-family:system-ui;color:#ef4444;">Failed to generate PDF. Please close this tab and try again.</p>';
      }
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleSendReport = async () => {
    if (!sendEmail) return;
    setSendError(null);

    // Parse fee input: empty → undefined (don't touch stored value),
    // otherwise a non-negative number. Backend rejects out-of-range.
    const feeTrimmed = sendFeeAmount.trim();
    let feeAmount: number | undefined;
    if (feeTrimmed) {
      const parsed = Number(feeTrimmed);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setSendError('Fee must be a non-negative number (e.g. 525 or 525.00).');
        return;
      }
      feeAmount = parsed;
    }

    try {
      setSendLoading(true);
      const response = await reportsApi.sendReport(reportId, {
        recipientEmail: sendEmail,
        agentName: sendAgentName.trim() || undefined,
        agentEmail: sendAgentEmail.trim() || undefined,
        feeAmount,
      });
      const data = response.data;
      const publishedAtIso = new Date().toISOString();
      setReport(prev =>
        prev
          ? {
              ...prev,
              status: 'sent',
              clientEmail: sendEmail,
              agentName: sendAgentName.trim() || prev.agentName || null,
              agentEmail: sendAgentEmail.trim() || prev.agentEmail || null,
              publishedAt: prev.publishedAt ?? publishedAtIso,
            }
          : null
      );
      setSendSuccess(true);
      setSendEmailSkipped(!data?.clientEmailSent);
      // Treat agent as skipped only if the inspector actually entered an
      // agent email and delivery to *that* address failed.
      setSendAgentSkipped(
        Boolean(sendAgentEmail.trim()) && data?.agentEmailSent === false
      );
    } catch (err) {
      console.error('Failed to send report:', err);
      // Surface the backend's validation message when present (e.g. "Agent
      // email is not a valid address") so the inspector knows what to fix.
      // Falls back to a generic line for network/5xx errors.
      const backendMessage =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: { data?: { error?: unknown } } }).response?.data?.error === 'string'
          ? ((err as { response: { data: { error: string } } }).response.data.error)
          : null;
      setSendError(backendMessage || 'Failed to send report. Please try again.');
    } finally {
      setSendLoading(false);
    }
  };

  const handleCopyPortalLink = async () => {
    const link = `${getPublicOrigin()}/r/${report!.publicUrl}`;
    const ok = await copyToClipboard(link);
    if (ok) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleMarkPaid = async () => {
    if (!report) return;
    try {
      setMarkPaidLoading(true);
      const response = await reportsApi.markPaid(reportId);
      if (response.success && response.data) {
        setReport(prev =>
          prev ? { ...prev, paymentReceivedAt: response.data!.paymentReceivedAt } : null
        );
      }
    } catch (err) {
      console.error('Failed to mark payment received:', err);
    } finally {
      setMarkPaidLoading(false);
    }
  };

  const openSendModal = () => {
    setSendEmail(report?.clientEmail || '');
    setSendAgentName(report?.agentName || '');
    setSendAgentEmail(report?.agentEmail || '');
    // pg returns NUMERIC as string; coerce to number, then format to 2dp
    // for display unless it's a round amount (525, not 525.00).
    const feeRaw = report?.feeAmount;
    if (feeRaw === null || feeRaw === undefined || feeRaw === '') {
      setSendFeeAmount('');
    } else {
      const n = Number(feeRaw);
      setSendFeeAmount(
        Number.isFinite(n) && n > 0
          ? n % 1 === 0 ? String(n) : n.toFixed(2)
          : ''
      );
    }
    setSendSuccess(false);
    setSendEmailSkipped(false);
    setSendAgentSkipped(false);
    setSendError(null);
    setShowSendModal(true);
  };

  const addSection = async () => {
    if (!report) return;
    const newSection = {
      title: 'New Section',
      order: report.sections.length,
      items: [],
    };
    await runSave(async () => {
      const response = await reportsApi.addSection(reportId, newSection);
      if (response.success && response.data) {
        setReport(prev => prev ? {
          ...prev,
          sections: [...prev.sections, response.data!]
        } : null);
      }
    });
  };

  // Memoized — child auto-save useEffects depend on this identity. A fresh
  // closure every render would re-fire the effect and re-schedule the save
  // on every parent re-render (including the renders our own saveState
  // transitions trigger), spamming the debounce timer.
  //
  // Errors propagate to the caller (the auto-save hook's runOne) so the
  // SaveState chip transitions to 'error' on failure. Wrapping in runSave
  // here would swallow the rejection.
  const updateSection = useCallback(async (sectionId: string, updates: Partial<ReportSection>) => {
    await reportsApi.updateSection(reportId, sectionId, updates);
    setReport(prev => prev ? {
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId ? { ...s, ...updates } : s
      )
    } : null);
  }, [reportId]);

  const deleteSection = (sectionId: string) => {
    setSectionPendingDelete(sectionId);
  };

  const confirmDeleteSection = async () => {
    const sectionId = sectionPendingDelete;
    if (!sectionId) return;
    setSectionPendingDelete(null);
    await runSave(async () => {
      await reportsApi.deleteSection(reportId, sectionId);
      setReport(prev => prev ? {
        ...prev,
        sections: prev.sections.filter(s => s.id !== sectionId)
      } : null);
    });
  };

  const addItem = async (sectionId: string) => {
    const newItem = {
      title: 'New Item',
      description: '',
      severity: null as Severity,
      photos: [],
      order: report?.sections.find(s => s.id === sectionId)?.items.length || 0,
    };
    await runSave(async () => {
      const response = await reportsApi.addItem(reportId, sectionId, newItem);
      if (response.success && response.data) {
        setReport(prev => prev ? {
          ...prev,
          sections: prev.sections.map(s =>
            s.id === sectionId
              ? { ...s, items: [...s.items, response.data!] }
              : s
          )
        } : null);
      }
    });
  };

  // Memoized — see updateSection above. Errors propagate to the auto-save
  // hook's runOne.
  const updateItem = useCallback(async (
    sectionId: string,
    itemId: string,
    updates: Partial<ReportItem>
  ) => {
    await reportsApi.updateItem(reportId, sectionId, itemId, updates);
    setReport(prev => prev ? {
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId
          ? {
              ...s,
              items: s.items.map(i =>
                i.id === itemId ? { ...i, ...updates } : i
              )
            }
          : s
      )
    } : null);
    if ('estimatedCost' in updates || 'estimatedCostLow' in updates || 'estimatedCostHigh' in updates) {
      setCostRefreshKey(k => k + 1);
    }
  }, [reportId]);

  const updateItemPhotos = (sectionId: string, itemId: string, photos: Photo[]) => {
    setReport(prev => prev ? {
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId
          ? { ...s, items: s.items.map(i => i.id === itemId ? { ...i, photos } : i) }
          : s
      )
    } : null);
  };

  const deleteItem = (sectionId: string, itemId: string) => {
    setItemPendingDelete({ sectionId, itemId });
  };

  const confirmDeleteItem = async () => {
    const pending = itemPendingDelete;
    if (!pending) return;
    const { sectionId, itemId } = pending;
    setItemPendingDelete(null);
    await runSave(async () => {
      await reportsApi.deleteItem(reportId, sectionId, itemId);
      setReport(prev => prev ? {
        ...prev,
        sections: prev.sections.map(s =>
          s.id === sectionId
            ? { ...s, items: s.items.filter(i => i.id !== itemId) }
            : s
        )
      } : null);
    });
  };

  const duplicateItem = async (sectionId: string, sourceItem: ReportItem) => {
    const newItem = {
      title: `${sourceItem.title} (copy)`,
      description: sourceItem.description,
      severity: sourceItem.severity,
      location: sourceItem.location,
      estimatedCost: sourceItem.estimatedCost,
      estimatedCostLow: sourceItem.estimatedCostLow,
      estimatedCostHigh: sourceItem.estimatedCostHigh,
      photos: [] as Photo[],
      order: report?.sections.find(s => s.id === sectionId)?.items.length || 0,
    };
    await runSave(async () => {
      const response = await reportsApi.addItem(reportId, sectionId, newItem);
      if (response.success && response.data) {
        setReport(prev => prev ? {
          ...prev,
          sections: prev.sections.map(s =>
            s.id === sectionId
              ? { ...s, items: [...s.items, response.data!] }
              : s
          )
        } : null);
      }
    });
  };

  const moveItem = useCallback((sectionId: string, dragIndex: number, hoverIndex: number) => {
    if (!report) return;

    setReport(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        sections: prev.sections.map(s => {
          if (s.id !== sectionId) return s;
          const newItems = [...s.items];
          const [dragged] = newItems.splice(dragIndex, 1);
          newItems.splice(hoverIndex, 0, dragged);
          const updated = newItems.map((item, idx) => ({ ...item, order: idx }));
          reportsApi.reorderItems(reportId, sectionId, updated.map(i => i.id));
          return { ...s, items: updated };
        })
      };
    });
  }, [report, reportId]);

  const moveSection = useCallback((dragIndex: number, hoverIndex: number) => {
    if (!report) return;

    const newSections = [...report.sections];
    const [draggedSection] = newSections.splice(dragIndex, 1);
    newSections.splice(hoverIndex, 0, draggedSection);

    const updatedSections = newSections.map((section, index) => ({
      ...section,
      order: index,
    }));

    setReport({ ...report, sections: updatedSections });
    reportsApi.reorderSections(reportId, updatedSections.map(s => s.id));
  }, [report, reportId]);

  const handleGenerateAiSummary = async () => {
    setAiSummaryError(null);
    setGeneratingAiSummary(true);
    try {
      const res = await reportsApi.generateAISummary(reportId);
      if (res.success && res.data?.summary) {
        const summary = res.data.summary;
        setAiSummaryText(summary);
        setReport(prev => prev ? { ...prev, aiSummary: summary, aiSummaryGeneratedAt: new Date().toISOString() } : null);
      } else {
        setAiSummaryError('No summary was returned. Please try again.');
      }
    } catch {
      setAiSummaryError('Failed to generate summary. Please try again.');
    } finally {
      setGeneratingAiSummary(false);
    }
  };

  // Debounced auto-save for the AI summary textarea. Skipped while a
  // regenerate is in flight — that codepath already writes server-side and
  // setting `aiSummaryText` from the response would otherwise re-trigger a
  // redundant PATCH.
  useEffect(() => {
    if (!report) return;
    if (generatingAiSummary) return;
    const serverValue = report.aiSummary ?? '';
    if (aiSummaryText === serverValue) return;
    scheduleSave(`report:${reportId}:aiSummary`, () =>
      reportsApi.updateAiSummary(reportId, aiSummaryText),
    );
  }, [aiSummaryText, report, generatingAiSummary, reportId, scheduleSave]);

  // Totals for the summary rail
  const totals = useMemo(() => {
    const result = {
      safety: 0,
      recommend: 0,
      total: 0,
      costLow: 0,
      costHigh: 0,
      hasCost: false,
    };
    if (!report) return result;
    for (const section of report.sections) {
      for (const item of section.items) {
        result.total++;
        const key = severityKey(item.severity);
        if (key === 'safety_concern') result.safety++;
        if (key === 'recommendation') result.recommend++;
        const low = item.estimatedCostLow ?? item.estimatedCost;
        const high = item.estimatedCostHigh ?? item.estimatedCost;
        if (low != null) { result.costLow += low; result.hasCost = true; }
        if (high != null) { result.costHigh += high; result.hasCost = true; }
      }
    }
    return result;
  }, [report]);


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading report...</div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">{error || 'Report not found'}</div>
      </div>
    );
  }

  const labels = LANGUAGE_CONFIGS[mode].severityLabels;

  const savedTimeLabel = lastSavedAt
    ? lastSavedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : null;
  const saveLabel =
    saveState === 'saving' ? 'Saving…'
    : saveState === 'error' ? 'Save failed'
    : saveState === 'saved' ? (savedTimeLabel ? `Saved ${savedTimeLabel}` : 'Saved')
    : savedTimeLabel ? `Saved ${savedTimeLabel}` : 'Saved';
  const saveClass =
    saveState === 'saving' ? 're-save re-save--saving'
    : saveState === 'error' ? 're-save re-save--error'
    : 're-save';

  return (
    <DndProvider backend={HTML5Backend}>
     <AutoSaveProvider value={autoSave}>
      <div className="re-root">
        {/* Sticky two-row header */}
        <header className="re-header">
          <div className="re-header-inner">
            <div className="re-header-row1">
              <nav className="re-breadcrumbs">
                <button onClick={() => navigate('/reports')} type="button">Reports</button>
                <IconChevRight size={12} />
                <span>{report.propertyAddress}</span>
              </nav>
            </div>
            <div className="re-header-row2">
              <div className="re-title-block">
                <div className="re-address">
                  <h1 className="re-address-title">{report.propertyAddress}</h1>
                </div>
                <div className="re-meta">
                  <strong>{report.clientName}</strong>
                  <span className="re-meta-dot">·</span>
                  <span>{formatDate(report.inspectionDate)}</span>
                </div>
              </div>
              <div className="re-actions">
                {/* Buyer/Seller mode toggle hidden until the semantics land —
                    today the only visible difference is "recommendation" vs
                    "suggested improvement", which is more confusing than
                    useful. The `mode` field stays in the data model so we
                    don't strand existing reports. Restore the segmented
                    control here once buyer-vs-seller actually reframes the
                    output. */}
                <span className={saveClass} role="status" aria-live="polite">
                  <span className="re-save-dot" />
                  {saveLabel}
                  {saveState === 'error' && (
                    <button
                      type="button"
                      onClick={retry}
                      className="re-save-retry"
                      aria-label="Retry save"
                    >
                      Retry
                    </button>
                  )}
                </span>
                <button
                  className="re-btn re-btn--secondary"
                  onClick={handleGeneratePdf}
                  disabled={generatingPdf}
                  type="button"
                >
                  {generatingPdf ? 'Generating…' : 'Generate PDF'}
                </button>
                {latestAgreement?.status === 'signed' && latestAgreement.signedPdfUrl ? (
                  <a
                    className="re-status re-status--signed"
                    href={latestAgreement.signedPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open the signed pre-inspection agreement PDF"
                  >
                    Agreement signed ✓{latestAgreement.signedAt
                      ? ` · ${formatDate(new Date(latestAgreement.signedAt))}`
                      : ''}
                  </a>
                ) : latestAgreement?.status === 'pending' ? (
                  <span className="re-status re-status--warn" title="Awaiting client signature">
                    Agreement awaiting signature
                  </span>
                ) : (
                  <span
                    className="re-status re-status--warn"
                    title="No signed agreement on file — send from the appointment"
                  >
                    Agreement not signed
                  </span>
                )}
                <button
                  className="re-btn re-btn--primary"
                  onClick={openSendModal}
                  type="button"
                >
                  Send to client
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Summary rail */}
        {report.sections.length > 0 && (
          <section className="re-summary">
            <div className="re-totals">
              <div className="re-totals-eyebrow">Totals</div>
              <div className="re-total">
                <span className="re-total-dot re-total-dot--safety" />
                <div>
                  <div className="re-total-num">{totals.safety}</div>
                  <div className="re-total-label">{labels.safety_concern}</div>
                </div>
              </div>
              <div className="re-total">
                <span className="re-total-dot re-total-dot--recommend" />
                <div>
                  <div className="re-total-num">{totals.recommend}</div>
                  <div className="re-total-label">{labels.recommendation}</div>
                </div>
              </div>
              <div className="re-total">
                <span className="re-total-dot re-total-dot--total" />
                <div>
                  <div className="re-total-num">{totals.total}</div>
                  <div className="re-total-label">Total items</div>
                </div>
              </div>
              <div className="re-total">
                <span className="re-total-dot re-total-dot--cost" />
                <div>
                  <div className="re-total-num">
                    {totals.hasCost
                      ? formatCostRange(totals.costLow, totals.costHigh)
                      : '—'}
                  </div>
                  <div className="re-total-label">Est. cost</div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Sections canvas */}
        <main className="re-canvas">
          {/* AI Summary — top of canvas so inspector can draft it before
              scrolling through sections */}
          <div className="re-ai-summary">
            <div className="re-ai-summary-head">
              <span className="re-ai-summary-eyebrow">AI Summary</span>
              {report.aiSummaryGeneratedAt && !generatingAiSummary && (
                <span className="re-ai-summary-hint">
                  Last generated {formatDate(report.aiSummaryGeneratedAt)}
                </span>
              )}
            </div>
            <div className="re-ai-summary-body">
              <textarea
                className="re-textarea re-ai-summary-textarea"
                value={aiSummaryText}
                onChange={(e) => setAiSummaryText(e.target.value)}
                placeholder="No summary yet — click 'Generate AI summary' to create one."
                rows={5}
                disabled={generatingAiSummary}
              />
              {generatingAiSummary ? (
                <div className="re-ai-summary-generating">
                  <span className="re-ai-summary-spinner" aria-hidden />
                  Generating summary — this takes about 15–30 seconds…
                </div>
              ) : (
                <button
                  className="re-btn re-btn--ai"
                  onClick={handleGenerateAiSummary}
                  type="button"
                >
                  <IconSparkle size={13} />
                  {aiSummaryText ? 'Regenerate' : 'Generate AI summary'}
                </button>
              )}
              {aiSummaryError && (
                <div className="re-ai-summary-error" role="alert">{aiSummaryError}</div>
              )}
            </div>
          </div>

          <div className="re-canvas-head">
            <span className="re-canvas-title">Sections</span>
            <span className="re-canvas-meta">{report.sections.length}</span>
            <div className="re-canvas-spacer" />
          </div>

          {sortByOrder(report.sections).map((section, index) => (
            <SectionCard
              key={section.id}
              section={section}
              index={index}
              mode={mode}
              propertyZip={report.propertyZip}
              onUpdate={(updates) => updateSection(section.id, updates)}
              onDelete={() => deleteSection(section.id)}
              onAddItem={() => addItem(section.id)}
              onUpdateItem={(itemId, updates) => updateItem(section.id, itemId, updates)}
              onUpdateItemPhotos={(itemId, photos) => updateItemPhotos(section.id, itemId, photos)}
              onDeleteItem={(itemId) => deleteItem(section.id, itemId)}
              onDuplicateItem={(item) => duplicateItem(section.id, item)}
              onMoveItem={(dragIdx, hoverIdx) => moveItem(section.id, dragIdx, hoverIdx)}
              onMove={moveSection}
            />
          ))}

          {report.sections.length > 0 && (
            <button className="re-add-section" onClick={addSection} type="button">
              <IconPlus size={14} />
              Add section
            </button>
          )}

          {report.sections.length === 0 && (
            <div className="re-empty">
              <div className="re-empty-icon">
                <IconFileDoc size={20} />
              </div>
              <h2>No sections yet</h2>
              <p>Add your first section to start building the report.</p>
              <button className="re-btn re-btn--primary" onClick={addSection} type="button">
                Add first section
              </button>
            </div>
          )}

          {report.sections.length > 0 && (
            <CostSummaryPanel reportId={reportId} refreshKey={costRefreshKey} />
          )}
        </main>
      </div>

      {/* Send to Client Modal */}
      {(() => {
        // Recompute the gate on every render so the Send button unlocks
        // immediately when mark-paid succeeds without closing/reopening the modal.
        // Inspectors who turn off require_payment_before_delivery skip the gate
        // entirely — the backend won't block the send, so neither should the UI.
        const feeForGate = sendFeeAmount.trim() ? Number(sendFeeAmount.trim()) : 0;
        const paymentGated =
          (profile?.requirePaymentBeforeDelivery ?? true) &&
          Number.isFinite(feeForGate) &&
          feeForGate > 0 &&
          !report?.paymentReceivedAt;

        return (
      <Modal
        open={showSendModal}
        onClose={() => setShowSendModal(false)}
        title="Send Report to Client"
        size="md"
        footer={
          sendSuccess ? null : (
            <>
              <Button variant="secondary" onClick={() => setShowSendModal(false)} disabled={sendLoading || markPaidLoading}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSendReport}
                disabled={!sendEmail || paymentGated}
                loading={sendLoading}
              >
                Send Report
              </Button>
            </>
          )
        }
      >
        {sendSuccess ? (
          <div className="text-center py-2">
            {sendEmailSkipped || sendAgentSkipped ? (
              <div className="w-12 h-12 bg-sev-minor-soft rounded-full flex items-center justify-center mx-auto mb-3">
                <svg
                  className="w-6 h-6 text-sev-minor-ink"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                </svg>
              </div>
            ) : (
              <div className="w-12 h-12 bg-brand-soft rounded-full flex items-center justify-center mx-auto mb-3">
                <svg
                  className="w-6 h-6 text-brand"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </div>
            )}
            {(() => {
              // Six outcome combinations across {client OK / failed} ×
              // {no agent / agent OK / agent failed}. Compute the heading and
              // body once, then render — avoids a nested ternary that's hard
              // to read or audit.
              const hasAgent = Boolean(sendAgentEmail.trim());
              const clientOk = !sendEmailSkipped;
              const agentOk = hasAgent && !sendAgentSkipped;
              const agentFailed = hasAgent && sendAgentSkipped;
              const allOk = clientOk && (agentOk || !hasAgent);
              const partial = (clientOk && agentFailed) || (!clientOk && agentOk);

              const heading = allOk
                ? 'Report sent!'
                : partial
                  ? 'Sent with issues'
                  : 'Email could not be sent';

              let body: string;
              if (clientOk && !hasAgent) {
                body = `${sendEmail} will receive the portal link.`;
              } else if (clientOk && agentOk) {
                body = `${sendEmail} and ${sendAgentEmail} will receive the portal link.`;
              } else if (clientOk && agentFailed) {
                body = `${sendEmail} received the portal link. Delivery to ${sendAgentEmail} failed — share the link with the agent manually.`;
              } else if (!clientOk && agentOk) {
                body = `${sendAgentEmail} received the portal link. Delivery to ${sendEmail} failed — share the link with the buyer manually.`;
              } else if (!clientOk && agentFailed) {
                body = `Delivery to ${sendEmail} and ${sendAgentEmail} both failed. You can share the portal link manually below.`;
              } else {
                // !clientOk && !hasAgent
                body = 'Email delivery failed or is not configured. You can share the portal link manually below.';
              }

              return (
                <>
                  <p className="text-body-md font-medium text-ink mb-1">{heading}</p>
                  <p className="text-body-sm text-ink-subtle mb-4">{body}</p>
                </>
              );
            })()}
            {report?.publicUrl && (
              <div className="flex flex-col items-center gap-2">
                <a
                  href={`/r/${report.publicUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-brand text-brand-ink shadow-xs hover:brightness-[0.96] active:brightness-[0.92] transition-all duration-180 ease-premium text-body-md font-medium"
                >
                  <svg
                    className="h-[18px] w-[18px]"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M15 3h6v6M10 14 21 3M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
                  </svg>
                  Open Client Portal
                </a>
                <Button
                  variant="secondary"
                  onClick={handleCopyPortalLink}
                  leadingIcon={
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  }
                >
                  {linkCopied ? 'Copied!' : 'Copy portal link'}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            {paymentGated && (
              <div className="mb-4 p-4 rounded-lg border border-[var(--warn-300)] bg-[var(--warn-50)]">
                <p className="text-body-sm font-medium text-[var(--warn-800)] mb-1">
                  Send is locked until you mark payment received.
                </p>
                <p className="text-body-xs text-[var(--warn-700)] mb-3">
                  The client sees your payment link on the portal and in the delivery email.
                </p>
                <Button
                  variant="secondary"
                  onClick={handleMarkPaid}
                  loading={markPaidLoading}
                >
                  Mark payment received
                </Button>
              </div>
            )}
            {report?.paymentReceivedAt && !paymentGated && (
              <div className="mb-4 px-3 py-2 rounded-md bg-[var(--brand-50)] border border-[var(--brand-200)] text-body-xs text-[var(--brand-700)]">
                Payment received {new Date(report.paymentReceivedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
            )}
            {sendError && (
              <div className="mb-4 p-3 bg-sev-major-soft text-sev-major-ink border border-sev-major/20 rounded-md text-body-sm">
                {sendError}
              </div>
            )}
            <div className="space-y-5">
              <div className="space-y-3">
                <p className="text-body-sm font-medium text-ink">Recipients</p>
                <Field label={`Client — ${report?.clientName || 'no name on file'}`} htmlFor="send-email">
                  <Input
                    id="send-email"
                    type="email"
                    value={sendEmail}
                    onChange={(e) => setSendEmail(e.target.value)}
                    placeholder="client@example.com"
                  />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Buyer's agent name" htmlFor="send-agent-name" optional>
                    <Input
                      id="send-agent-name"
                      value={sendAgentName}
                      onChange={(e) => setSendAgentName(e.target.value)}
                      placeholder="Agent name"
                    />
                  </Field>
                  <Field label="Buyer's agent email" htmlFor="send-agent-email" optional>
                    <Input
                      id="send-agent-email"
                      type="email"
                      value={sendAgentEmail}
                      onChange={(e) => setSendAgentEmail(e.target.value)}
                      placeholder="agent@example.com"
                    />
                  </Field>
                </div>
                <Field
                  label="Inspection fee (USD)"
                  htmlFor="send-fee-amount"
                  optional
                  hint="Leave blank to skip the 'How to pay' block. Set your payment instructions in Settings."
                >
                  <Input
                    id="send-fee-amount"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    value={sendFeeAmount}
                    onChange={(e) => setSendFeeAmount(e.target.value)}
                    placeholder="525"
                  />
                </Field>
              </div>

              {report.sections.length > 0 && (
                <div className="rounded-md border border-line bg-surface-subtle p-4">
                  <p className="text-body-sm font-medium text-ink mb-2">At a glance</p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-body-sm text-ink-subtle">
                    <span>
                      <strong className="text-sev-major-ink">{totals.safety}</strong>{' '}
                      {totals.safety === 1 ? labels.safety_concern.replace(/s$/, '') : labels.safety_concern}
                    </span>
                    <span aria-hidden>·</span>
                    <span>
                      <strong className="text-ink">{totals.recommend}</strong>{' '}
                      {totals.recommend === 1 ? labels.recommendation.replace(/s$/, '') : labels.recommendation}
                    </span>
                    <span aria-hidden>·</span>
                    <span>
                      <strong className="text-ink">{totals.total}</strong> item{totals.total === 1 ? '' : 's'} inspected
                    </span>
                  </div>
                </div>
              )}

              {report?.publicUrl && (
                <div className="text-body-sm text-ink-subtle">
                  <span className="block mb-1 text-ink">Portal link recipients will receive</span>
                  <code className="break-all text-body-xs text-ink">
                    {`${getPublicOrigin()}/r/${report.publicUrl}`}
                  </code>
                </div>
              )}
            </div>
          </>
        )}
      </Modal>
        );
      })()}

      {/* Capture FABs (camera, voice) live in the field/inspection view
          (InspectionView), not here — once the inspector is compiling the
          report, those affordances just add noise. */}
      <ConfirmDialog
        isOpen={sectionPendingDelete !== null}
        title="Delete section?"
        message="This will permanently remove the section and every item inside it. This cannot be undone."
        confirmLabel="Delete section"
        cancelLabel="Cancel"
        destructive
        onConfirm={confirmDeleteSection}
        onCancel={() => setSectionPendingDelete(null)}
      />
      <ConfirmDialog
        isOpen={itemPendingDelete !== null}
        title="Delete item?"
        message="This will permanently remove the item and its photos. This cannot be undone."
        confirmLabel="Delete item"
        cancelLabel="Cancel"
        destructive
        onConfirm={confirmDeleteItem}
        onCancel={() => setItemPendingDelete(null)}
      />
     </AutoSaveProvider>
    </DndProvider>
  );
};


export default ReportBuilder;
